import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearTOTPSession } from '../utils/totpSession';
import AttestationBox from './common/AttestationBox';
import {
  NO_PET_ATTESTATION_TEXT,
  NO_PET_ATTESTATION_TITLE,
  buildAttestationData,
  type AttestationData,
} from '../constants/attestation';

export interface NoPetDeclarationData {
  phone: string;
  attestation: AttestationData;
}

interface NoPetProps {
  onClose: () => void;
  /** Prefilled phone for registration (editable) */
  initialPhone?: string;
  /** Called when user clicks Done with attestation acknowledged; use to send data to backend */
  onNoPetDeclare?: (data: NoPetDeclarationData) => void;
}

const NoPet: React.FC<NoPetProps> = ({ onClose, initialPhone = '', onNoPetDeclare }) => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState(initialPhone);
  const [attestationAcknowledged, setAttestationAcknowledged] = useState(false);
  const [highlightCheckbox, setHighlightCheckbox] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const performLogout = () => {
    localStorage.removeItem('authToken');
    clearTOTPSession();
    sessionStorage.removeItem('sessionActive');
    navigate('/login', { replace: true });
  };

  // Prefill phone when initialPhone is provided after mount (e.g. from async)
  useEffect(() => {
    if (initialPhone && !phone) setPhone(initialPhone);
  }, [initialPhone]);

  // Clear highlight when user checks the checkbox
  useEffect(() => {
    if (attestationAcknowledged && highlightCheckbox) {
      setHighlightCheckbox(false);
    }
  }, [attestationAcknowledged]);

  // Submit attestation to backend
  const submitAttestation = async () => {
    const attestation = buildAttestationData(NO_PET_ATTESTATION_TEXT);
    try {
      await Promise.resolve(onNoPetDeclare?.({ phone, attestation }));
      return true;
    } catch (err) {
      console.error('Failed to submit no-pet attestation:', err);
      return false;
    }
  };

  const handleDone = async () => {
    // If checkbox not checked, highlight it and return
    if (!attestationAcknowledged) {
      setHighlightCheckbox(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await submitAttestation();
      if (success) {
        // Show thank you message with logout button
        setShowThankYou(true);
      }
      setIsSubmitting(false);
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
      <div className="w-full h-full bg-gray-50 flex items-center justify-center overflow-y-auto p-4">
        <div className="flex flex-col items-center space-y-6 px-4 max-w-md w-full">
          {/* Brand logo */}
          <div className="flex items-center">
            <img
              src="/landing/getpawtected.svg"
              alt="GetPawtected Logo"
              className="h-12 sm:h-16 w-auto drop-shadow-sm"
            />
          </div>

          {/* Thank you message */}
          <div className="text-center space-y-2">
            <p
              className="text-xl sm:text-2xl text-black font-medium"
              style={{ fontFamily: 'Poppins, sans-serif', letterSpacing: '-0.05em' }}
            >
              Thank you for your response!
            </p>
          </div>

          {/* No-pet attestation: checkbox must be checked to enable Done */}
          <div className="w-full max-w-sm">
            <h3 className="text-base font-semibold text-gray-900 mb-3">{NO_PET_ATTESTATION_TITLE}</h3>
            <AttestationBox
              text={NO_PET_ATTESTATION_TEXT}
              checked={attestationAcknowledged}
              onCheckedChange={setAttestationAcknowledged}
              label="I have read and agree to the above declaration."
              id="no-pet-attestation"
              required
              highlight={highlightCheckbox}
            />
          </div>

          {/* Done button */}
          <div className="w-full max-w-sm pt-2">
            {!showThankYou && (
              <button
                type="button"
                onClick={handleDone}
                disabled={isSubmitting}
                className={`relative w-full min-h-[48px] px-6 py-3 rounded-full font-semibold overflow-hidden transition-all focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 border-2 border-purple-600 ${
                  isSubmitting
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[color:var(--pmc-primary)] text-white hover:opacity-95'
                }`}
                style={{ fontFamily: 'Poppins, sans-serif' }}
                aria-label="Done, submit attestation"
              >
                <span className="relative z-10">
                  {isSubmitting ? 'Submitting...' : 'Done'}
                </span>
              </button>
            )}

            {/* Confirmation screen after successful submission */}
            {showThankYou && (
              <div className="p-6 bg-green-50 border border-green-200 rounded-xl text-center space-y-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-lg font-semibold text-green-800">
                    Declaration Submitted Successfully
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Your "No Pets" declaration has been recorded. A confirmation email has been sent.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-colors"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    Stay Logged In
                  </button>
                  <button
                    type="button"
                    onClick={performLogout}
                    className="flex-1 px-4 py-2.5 bg-[color:var(--pmc-primary)] text-white rounded-full font-medium hover:opacity-95 transition-colors"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    Log Out Now
                  </button>
                </div>
              </div>
            )}

            {/* Hint when checkbox is highlighted */}
            {highlightCheckbox && !attestationAcknowledged && !showThankYou && (
              <p className="text-xs text-red-500 text-center mt-2">
                Please check the box above to confirm your attestation
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoPet;
